import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBuilderStore } from '../../stores/builderStore';
import {
  Search, Plus, Star, Layout, Type, Image, Video, Grid, MessageSquare,
  ArrowRight, Zap, X, Sparkles, Crown, Play, Monitor, Smartphone
} from 'lucide-react';
import { premiumTemplates, templateCategories } from './premiumTemplates';

// Original component library (keeping for standard components)
import { COMPONENT_LIBRARY } from './ComponentLibrary';

interface ComponentLibraryProps {
  onClose?: () => void;
}

export const EnhancedComponentLibrary: React.FC<ComponentLibraryProps> = ({ onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [showPremium, setShowPremium] = useState(true); // Default to premium
  const addSection = useBuilderStore(state => state.addSection);

  // Debug: Log all content templates
  console.log('All premium templates:', premiumTemplates.length);
  console.log('Content templates:', premiumTemplates.filter(t => t.category === 'content').map(t => ({
    id: t.id,
    name: t.name,
    category: t.category
  })));

  const categories = showPremium
    ? templateCategories.map(c => c.name)
    : ['all', 'Layout', 'Content', 'Navigation', 'Social Proof', 'Media'];

  const handleAddPremiumTemplate = (template: any) => {
    addSection(template.config.type, template.config);
    onClose?.();
  };

  const handleAddStandardSection = (variant: any) => {
    // For features sections, default to netflix variant for horizontal scrolling
    const config = variant.config.type === 'features' || variant.config.type === 'feature_cards'
      ? {
          ...variant.config,
          props: {
            ...variant.config.props,
            variant: variant.config.props?.variant || 'netflix' // Default to netflix for horizontal scroll
          }
        }
      : variant.config;

    addSection(config.type, config);
    onClose?.();
  };

  const filteredPremiumTemplates = premiumTemplates.filter(template => {
    const matchesSearch = searchTerm === '' ||
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (template.description?.toLowerCase().includes(searchTerm.toLowerCase()) || false);

    const normalizedCategory = activeCategory.toLowerCase()
      .replace(' sections', '')
      .replace('call to action', 'cta')
      .replace('content blocks', 'content');

    const categoryMatch = activeCategory === 'All Templates' ||
      template.category === normalizedCategory;

    // Debug logging for content category
    if (activeCategory === 'Content Blocks') {
      console.log('Filtering for Content Blocks:', {
        templateId: template.id,
        templateCategory: template.category,
        normalizedCategory,
        categoryMatch,
        activeCategory
      });
    }

    return matchesSearch && categoryMatch;
  });

  const filteredStandardSections = Object.entries(COMPONENT_LIBRARY.sections).filter(([key, section]) => {
    const matchesSearch = section.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          section.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'all' || section.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="w-96 bg-white dark:bg-gray-900 h-full flex flex-col shadow-xl">
      {/* Premium Header with Gradient */}
      <div className="p-4 border-b dark:border-gray-700 bg-gradient-to-r from-purple-600 to-blue-600">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-xl text-white flex items-center gap-2">
            {showPremium ? (
              <>
                <Crown className="w-6 h-6 text-yellow-300 animate-pulse" />
                Premium Templates
              </>
            ) : (
              <>
                <Grid className="w-6 h-6" />
                Components
              </>
            )}
          </h3>
          {onClose && (
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Toggle Premium/Standard with Animation */}
        <div className="flex gap-2">
          <motion.button
            onClick={() => setShowPremium(false)}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
              !showPremium
                ? 'bg-white text-gray-900 shadow-lg'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Standard
          </motion.button>
          <motion.button
            onClick={() => setShowPremium(true)}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              showPremium
                ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white shadow-lg'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Sparkles className="w-4 h-4" />
            Premium
          </motion.button>
        </div>
      </div>

      {/* Search with Glass Effect */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder={showPremium ? "Search premium templates..." : "Search components..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-900"
          />
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 mt-3">
          {categories.map(cat => (
            <motion.button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                activeCategory === cat
                  ? showPremium
                    ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-md'
                    : 'bg-blue-500 text-white shadow-md'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {cat}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Content Area with Animation */}
      <div className="flex-1 overflow-y-auto p-4 scrollbar-thin">
        <AnimatePresence mode="wait">
          {showPremium ? (
            <motion.div
              key="premium"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              {filteredPremiumTemplates.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No templates found matching your criteria
                </div>
              ) : (
                filteredPremiumTemplates.map(template => (
                  <motion.div
                    key={template.id}
                    className="relative group"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="glass-card overflow-hidden">
                      {/* Template Preview Area */}
                      <div className="relative h-40 bg-gradient-to-br from-purple-100 to-blue-100 dark:from-purple-900/20 dark:to-blue-900/20 overflow-hidden">
                        {/* Animated Background Pattern */}
                        <div className="absolute inset-0 opacity-10">
                          <div className="absolute inset-0 mesh-gradient-bg" />
                        </div>

                        {/* Template Icon/Preview */}
                        <div className="relative h-full flex items-center justify-center">
                          {template.config.type === 'hero' && template.config.variant === 'netflix' && (
                            <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                              <Play className="w-12 h-12 text-red-600" />
                              <span className="text-xl font-bold">Netflix Style</span>
                            </div>
                          )}
                          {template.config.type === 'hero' && template.config.variant === 'appstore' && (
                            <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                              <Monitor className="w-12 h-12 text-blue-600" />
                              <span className="text-xl font-bold">App Store Style</span>
                            </div>
                          )}
                          {template.config.type === 'features' && (
                            <div className="grid grid-cols-3 gap-2">
                              {[1, 2, 3].map(i => (
                                <div key={i} className="w-12 h-12 bg-white/50 rounded-lg animate-pulse" />
                              ))}
                            </div>
                          )}
                          {template.config.type === 'cta' && (
                            <div className="text-center">
                              <div className="w-32 h-10 bg-white/50 rounded-lg mb-2 animate-pulse" />
                              <div className="w-20 h-8 bg-purple-500/50 rounded-lg mx-auto animate-pulse" />
                            </div>
                          )}
                          {template.config.type === 'content' && (
                            <div className="flex items-center justify-center gap-4">
                              {template.config.variant === 'magazine' && (
                                <div className="text-center">
                                  <Type className="w-12 h-12 text-purple-600 mb-2" />
                                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Magazine</span>
                                </div>
                              )}
                              {template.config.variant === 'blog' && (
                                <div className="text-center">
                                  <Image className="w-12 h-12 text-blue-600 mb-2" />
                                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Blog Post</span>
                                </div>
                              )}
                              {template.config.variant === 'faq' && (
                                <div className="text-center">
                                  <MessageSquare className="w-12 h-12 text-green-600 mb-2" />
                                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">FAQ</span>
                                </div>
                              )}
                              {template.config.variant === 'feature' && (
                                <div className="text-center">
                                  <Layout className="w-12 h-12 text-indigo-600 mb-2" />
                                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Feature</span>
                                </div>
                              )}
                              {template.config.variant === 'quote' && (
                                <div className="text-center">
                                  <MessageSquare className="w-12 h-12 text-teal-600 mb-2" />
                                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Testimonials</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Premium Badge */}
                        <div className="absolute top-2 right-2">
                          <span className="px-2 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold rounded-full shadow-lg">
                            PREMIUM
                          </span>
                        </div>
                      </div>

                      {/* Template Info */}
                      <div className="p-4">
                        <h4 className="font-bold text-lg mb-1 text-gray-900 dark:text-gray-100">
                          {template.name}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          {template.description}
                        </p>

                        {/* Action Button with Gradient */}
                        <motion.button
                          onClick={() => handleAddPremiumTemplate(template)}
                          className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg shadow-md hover:shadow-xl transition-all flex items-center justify-center gap-2"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Plus className="w-4 h-4" />
                          Add to Page
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </motion.div>
          ) : (
            <motion.div
              key="standard"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-6"
            >
              {filteredStandardSections.map(([key, section]) => {
                const Icon = section.icon;
                return (
                  <div key={key} className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="w-5 h-5 text-gray-600" />
                      <h4 className="font-medium">{section.name}</h4>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">{section.description}</p>

                    {/* Variants */}
                    <div className="space-y-2">
                      {section.variants.map(variant => (
                        <motion.button
                          key={variant.id}
                          onClick={() => handleAddStandardSection(variant)}
                          className="w-full p-3 border rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors group text-left"
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{variant.preview}</span>
                              <span className="text-sm font-medium">{variant.name}</span>
                            </div>
                            <Plus className="w-4 h-4 text-gray-400 group-hover:text-blue-500" />
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};